/* eslint-disable import/order */
import type { EntityReference } from '@/shared/types/task.js'
import { trait } from '@traits-ts/core'

/**
 * WithReferences trait
 * Adds reference tracking capabilities to tasks using EntityReference objects
 */
export const WithReferences = trait(superclass => class X extends superclass {
  references: EntityReference[] = []

  /**
   * Add a reference to another entity
   * @param entityId ID of the entity
   * @param entityType Type of entity (member, subkrewe, etc)
   * @param referenceType Purpose of the reference (creator, assignee, related, etc)
   */
  addReference(entityId: string, entityType: string, referenceType: string) {
    // Don't add duplicate references
    if (!this.hasReference(entityId, entityType, referenceType)) {
      this.references.push({
        entityId,
        entityType,
        referenceType,
        createdAt: new Date(),
      })
    }
    return this
  }

  /**
   * Remove a reference to an entity
   * @param entityId ID of the entity
   * @param entityType Type of entity
   * @param referenceType Optional reference type to match
   */
  removeReference(entityId: string, entityType: string, referenceType?: string) {
    this.references = this.references.filter(ref =>
      !(ref.entityId === entityId
        && ref.entityType === entityType
        && (referenceType ? ref.referenceType === referenceType : true)),
    )
    return this
  }

  /**
   * Check if this task references a specific entity
   * @param entityId ID of the entity
   * @param entityType Type of entity
   * @param referenceType Optional reference type to match
   */
  hasReference(entityId: string, entityType: string, referenceType?: string): boolean {
    return this.references.some(ref =>
      ref.entityId === entityId
      && ref.entityType === entityType
      && (referenceType ? ref.referenceType === referenceType : true),
    )
  }

  /**
   * Get all references of a specific type
   * @param referenceType Type of reference to filter by
   */
  getReferencesByType(referenceType: string): EntityReference[] {
    return this.references.filter(ref => ref.referenceType === referenceType)
  }

  /**
   * Get all references to a specific entity type
   * @param entityType Type of entity to filter by
   */
  getReferencesByEntityType(entityType: string): EntityReference[] {
    return this.references.filter(ref => ref.entityType === entityType)
  }

  /**
   * For backward compatibility - get a reference value by key
   * @deprecated Use more specific reference methods instead
   * @param key Reference key
   */
  getReference(key: string): string | undefined {
    const ref = this.references.find(r => r.referenceType === key)
    return ref?.entityId
  }
})
